import { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import { InternalReport, InternalReportFile, InternalReportGame, InternalReportMutation, InternalReportTag } from '../features/reports/types/InternalReport'
import { InternalTagType } from '../types/InternalTag'
import { jsonAnswer } from './helpers'
import { internalApiserverApi } from './internal-apiserver'

const reportGameFields = ['appId', 'id', 'name', 'artist', 'icon', 'icons']

export const reportFileName = (id: string, nameHeader: string | null) => nameHeader || `GameRefinery-Report-${id}.pdf`

const reportsApi = internalApiserverApi.enhanceEndpoints({ addTagTypes: ['internalReports'] }).injectEndpoints({
  endpoints: (builder) => ({
    getInternalReports: builder.query<InternalReport[], void>({
      query: () => 'i/reports/',
      providesTags: ['internalReports'],
    }),
    getInternalReport: builder.query<InternalReport, string>({
      query: (id) => `i/reports/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'internalReports', id }],
    }),
    // Both answer the stored report. Neither invalidates: the page reads the report back first, and a refetch
    // started by invalidation makes that read resolve right away with the report from before the save.
    // The page calls `invalidateInternalReports` after reading back.
    createInternalReport: builder.mutation<InternalReport, InternalReportMutation>({
      query: (body) => ({ url: 'i/reports', method: 'POST', body, headers: jsonAnswer }),
    }),
    updateInternalReport: builder.mutation<InternalReport, InternalReportMutation>({
      query: (body) => ({ url: 'i/reports', method: 'PUT', body, headers: jsonAnswer }),
    }),
    // Also removes the report's permission row.
    deleteInternalReport: builder.mutation<string, string>({
      query: (id) => ({ url: `i/reports/${id}`, method: 'DELETE', responseHandler: 'text', headers: jsonAnswer }),
      // Not the list: the page refetches it to confirm the delete, and a refetch started by invalidation
      // makes that refetch resolve right away with the list from before the delete.
      invalidatesTags: (_result, _error, id) => [{ type: 'internalReports', id }],
    }),
    uploadInternalReportFile: builder.mutation<InternalReportFile, File>({
      query: (file) => {
        const body = new FormData()
        body.set('key', file.name)
        body.set('file', file)
        body.set('type', 'report')
        return { url: 'i/files/upload/', method: 'POST', body, headers: jsonAnswer }
      },
    }),
    // Saves the PDF in the browser and answers its file name.
    downloadInternalReportFile: builder.mutation<string, string>({
      queryFn: async (id, _api, _extraOptions, baseQuery) => {
        let nameHeader: string | null = null
        const result = await baseQuery({
          url: `i/reports/${id}/file`,
          responseHandler: (response) => {
            nameHeader = response.headers.get('Name')
            return response.blob()
          },
        })
        if (result.error) return { error: result.error as FetchBaseQueryError }
        const fileName = reportFileName(id, nameHeader)
        const url = URL.createObjectURL(result.data as Blob)
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        link.click()
        setTimeout(() => URL.revokeObjectURL(url), 0)
        return { data: fileName }
      },
    }),
    getInternalReportTags: builder.query<InternalReportTag[], void>({
      query: () => ({ url: 'i/tags/list', params: { type: InternalTagType.Report } }),
      providesTags: [{ type: 'internalTags', id: InternalTagType.Report }],
    }),
    // Game ids, as legacy's list. Ids with no game are left out.
    getInternalReportGames: builder.query<InternalReportGame[], string[]>({
      query: (gameIds) => ({ url: 'games/list', method: 'POST', body: { gameIds, include: reportGameFields.join(',') } }),
    }),
  }),
})

export const invalidateInternalReports = () => reportsApi.util.invalidateTags(['internalReports'])

export const {
  useGetInternalReportsQuery,
  useGetInternalReportQuery,
  useLazyGetInternalReportQuery,
  useLazyGetInternalReportsQuery,
  useCreateInternalReportMutation,
  useUpdateInternalReportMutation,
  useDeleteInternalReportMutation,
  useUploadInternalReportFileMutation,
  useDownloadInternalReportFileMutation,
  useGetInternalReportTagsQuery,
  useGetInternalReportGamesQuery,
} = reportsApi
